import type { ComponentProps, MouseEvent } from 'react';
import { Text } from '@lmn/design-system';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { Icon } from '@/app/common/icons/Icon';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { CancellationLabel, EssentialRow, InfoLabelButton } from './EvoRoomCard.styled';

type EvoRoomInfoLabelProps = {
  /** Row label; when an info action is present the whole label doubles as the trigger. */
  label: string;
  /** Label colour token. */
  color: ComponentProps<typeof Text>['color'];
  /** Trailing info icon — omit (or omit `onInfoClick`) to render a plain, non-clickable label. */
  infoIcon?: StarfishIconRef;
  onInfoClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

type EvoRoomInfoRowProps = EvoRoomInfoLabelProps & {
  /** Leading 20px status icon. */
  icon: StarfishIconRef;
};

/**
 * Shared "icon + label + optional info action" row used by the cancellation policy line and each room
 * promotion. When `onInfoClick` is provided, the label and the trailing info icon form a single
 * clickable button (its accessible name is the label text), so tapping either one opens the related
 * modal. The info icon is bound to the label's last word so it always flows inline, even when the
 * label wraps to a second line.
 */
export function EvoRoomInfoRow({ icon, ...labelProps }: EvoRoomInfoRowProps) {
  return (
    <EssentialRow>
      <Icon {...icon} size={20} css={'margin-top: 2px;'} />
      <EvoRoomInfoLabel {...labelProps} />
    </EssentialRow>
  );
}

/**
 * The label half of EvoRoomInfoRow, for rows whose leading icon is not an image — the meal plan line
 * renders a design-system svg — but that still want the same label-as-info-trigger behaviour.
 */
export function EvoRoomInfoLabel({ label, color, infoIcon, onInfoClick }: EvoRoomInfoLabelProps) {
  const showInfo = !!infoIcon && !!onInfoClick;

  // AddElementToLastWordInString flex-centres the icon beside the last word only for multi-word labels
  // (e.g. "Free cancellation until…"). A single-word label (e.g. "Non-refundable") renders as plain inline
  // text where the baseline-aligned icon rides high, so nudge only that case down with position/top — which
  // leaves the line box intact (margin/vertical-align would drag the whole label down).
  const isSingleWordLabel = label.split(' ').length === 1;
  const infoIconCss = `margin-left: 4px;${isSingleWordLabel ? ' position: relative; top: 2px;' : ''}`;

  const labelContent = (
    <Text variant="Body" color={color} forceTag="span">
      {/* Bind the info icon to the last word so it never wraps onto its own line. */}
      <AddElementToLastWordInString text={label}>
        {showInfo ? <Icon {...infoIcon} size={16} css={infoIconCss} /> : null}
      </AddElementToLastWordInString>
    </Text>
  );

  return (
    <CancellationLabel>
      {showInfo ? (
        <InfoLabelButton type="button" onClick={onInfoClick}>
          {labelContent}
        </InfoLabelButton>
      ) : (
        labelContent
      )}
    </CancellationLabel>
  );
}
